<template>
  <print-preview @reset="reset" :landscape="landscape">
    <div slot="pages">
      <page :styles="styles" :title="dailyReport.title" :brnch_name="dailyReport.branch_name">
        <template slot="header">
          <th colspan="100%" class="py-2" />
        </template>

        <template slot="body">
          <template v-if="!dailyReport.lctr">
            <!-- credits -->
            <tr v-if="!dailyReport.lctr">
              <td
                colspan="100%"
                class="secondary white--text px-3 body-2 lr--text"
              >
                <strong>{{ $t("cash_receipts") }}</strong>
              </td>
            </tr>

            <tr class="pb-2">
              <td style="max-width: 1cm">
                <strong> {{ $t("number") }} </strong>
              </td>
              <td
                v-for="(item, i) in headers"
                :key="'cc_' + i"
                :colspan="item.colspan"
              >
                <strong>{{ item.text }}</strong>
              </td>
            </tr>
            <tr v-for="(item, index) in items.credits" :key="'ca_' + index">
              <td style="max-width: 1cm">{{ index + 1 }}</td>
              <td>CR_{{ item.id }}</td>
              <td>{{ item.date | persianDateFilter }}</td>
              <td>{{ item.customer.name }}</td>
              <td colspan="2">
                {{ item.amount | currency({ symbol: smbl(item.currency_id) }) }}
              </td>
              <td>{{ item.rate }}</td>
              <td>
                {{
                  item.exchanged
                    | currency({ symbol: smbl(item.receiver_currency_id) })
                }}
              </td>
              <td>{{ item.description }}</td>
            </tr>

            <!-- debits -->
            <tr>
              <td colspan="100%" class="pt-4"></td>
            </tr>
            <tr>
              <td
                colspan="100%"
                class="secondary lr--text white--text px-3 body-1"
              >
                <strong>{{ $t("cash_payments") }}</strong>
              </td>
            </tr>
            <tr class="pb-2">
              <td style="max-width: 1cm">
                <strong> {{ $t("number") }} </strong>
              </td>

              <td
                v-for="(item, i) in headers"
                :key="'dc_' + i"
                :colspan="item.colspan"
              >
                <strong>{{ item.text }}</strong>
              </td>
            </tr>
            <tr v-for="(item, index) in items.debits" :key="'da_' + index">
              <td style="max-width: 1cm">{{ index + 1 }}</td>
              <td>DB_{{ item.id }}</td>
              <td>{{ item.date | persianDateFilter }}</td>
              <td>{{ item.customer.name }}</td>
              <td colspan="2">
                {{ item.amount | currency({ symbol: smbl(item.currency_id) }) }}
              </td>
              <td>{{ item.rate }}</td>
              <td>
                {{
                  item.exchanged
                    | currency({ symbol: smbl(item.receiver_currency_id) })
                }}
              </td>
              <td>{{ item.description }}</td>
            </tr>
          </template>

          <!-- exchanges -->
          <tr v-if="!dailyReport.lctr">
            <td colspan="100%" class="pt-4"></td>
          </tr>
          <tr>
            <td
              colspan="100%"
              class="secondary lr--text white--text px-3 body-1"
            >
              <strong>{{ $t("exchanges") }}</strong>
            </td>
          </tr>
          <tr class="pb-2">
            <td style="max-width: 1cm">
              <strong> {{ $t("number") }} </strong>
            </td>

            <td
              v-for="(item, i) in exchange_headers"
              :key="'ec_' + i"
              :colspan="item.colspan"
            >
              <strong>{{ item.text }}</strong>
            </td>
          </tr>
          <tr v-for="(item, index) in items.exchanges" :key="'ea_' + index">
            <td style="max-width: 1cm">{{ index + 1 }}</td>
            <td>EX_{{ item.id }}</td>
            <td>{{ item.date | persianDateFilter }}</td>
            <td>{{ item.details.name }}</td>
            <td colspan="2">
              {{ item.amount | currency({ symbol: smbl(item.sale_currency) }) }}
            </td>
            <td>{{ item.rate }}</td>
            <td>
              {{
                item.exchanged_amount
                  | currency({ symbol: smbl(item.buy_currency) })
              }}
            </td>
            <td>{{ item.description }}</td>
          </tr>

          <!-- send transfers -->
          <tr>
            <td colspan="100%" class="pt-4"></td>
          </tr>
          <tr>
            <td
              colspan="100%"
              class="secondary lr--text white--text px-3 body-1"
            >
              <strong>{{ $t("send_transfers") }}</strong>
            </td>
          </tr>
          <tr class="pb-2">
            <td style="max-width: 1cm">
              <strong> {{ $t("number") }} </strong>
            </td>

            <td
              v-for="(item, i) in transfer_headers"
              :key="'sth_' + i"
              :colspan="item.colspan"
            >
              <strong>{{ item.text }}</strong>
            </td>
          </tr>
          <tr
            v-for="(item, index) in items.send_transfers"
            :key="'std_' + index"
          >
            <td style="max-width: 1cm">{{ index + 1 }}</td>
            <td>{{ item.date }}</td>
            <td>{{ item.number }}</td>
            <td>{{ item.sender.name }}</td>
            <td>{{ item.receiver.name }}</td>
            <td colspan="2">
              {{ item.amount | currency({ symbol: smbl(item.currency_id) }) }}
            </td>
            <td>
              {{
                item.commission_amount
                  | currency({ symbol: smbl(item.commission_currency_id) })
              }}
            </td>
            <td>{{ item.description }}</td>
          </tr>

          <!-- received_transfers -->
          <tr>
            <td colspan="100%" class="pt-4"></td>
          </tr>
          <tr>
            <td
              colspan="100%"
              class="secondary lr--text white--text px-3 body-1"
            >
              <strong>{{ $t("received_transfers") }}</strong>
            </td>
          </tr>
          <tr class="pb-2">
            <td style="max-width: 1cm">
              <strong> {{ $t("number") }} </strong>
            </td>

            <td
              v-for="(item, i) in received_transfer_headers"
              :key="'reh_' + i"
              :colspan="item.colspan"
            >
              <strong>{{ item.text }}</strong>
            </td>
          </tr>
          <tr
            v-for="(item, index) in items.received_transfers"
            :key="'red_' + index"
          >
            <td style="max-width: 1cm">{{ index + 1 }}</td>
            <td>{{ item.date }}</td>
            <td>{{ item.number }}</td>
            <td>{{ item.sender.name }}</td>
            <td>{{ item.receiver.name }}</td>
            <td>
              {{ item.amount | currency({ symbol: smbl(item.currency_id) }) }}
            </td>
            <td>
              {{
                item.commission_amount
                  | currency({ symbol: smbl(item.commission_currency_id) })
              }}
            </td>
            <td>
              {{
                item.done == 1 ? $t("transfer_done") : $t("transfer_not_done")
              }}
            </td>
            <td>{{ item.description }}</td>
          </tr>

          <template v-if="!dailyReport.lctr">
            <!-- a2a transfers -->
            <tr>
              <td colspan="100%" class="pt-4"></td>
            </tr>
            <tr>
              <td
                colspan="100%"
                class="secondary lr--text white--text px-3 body-1"
              >
                <strong>{{ $t("a2a_transfers") }}</strong>
              </td>
            </tr>
            <tr class="pb-2">
              <td style="max-width: 1cm">
                <strong> {{ $t("number") }} </strong>
              </td>
              <td
                v-for="(item, i) in a2a_transfer_headers"
                :key="'a2ah_' + i"
                :colspan="item.colspan"
              >
                <strong>{{ item.text }}</strong>
              </td>
            </tr>
            <tr
              v-for="(item, index) in items.a2a_transfers"
              :key="'a2ad_' + index"
            >
              <td style="max-width: 1cm">{{ index + 1 }}</td>
              <td>{{ item.date }}</td>
              <td>{{ item.number }}</td>
              <td>{{ item.sender.name }}</td>
              <td>{{ item.receiver.name }}</td>
              <td>
                {{ item.amount | currency({ symbol: smbl(item.currency_id) }) }}
              </td>
              <td>{{ item.rate }}</td>
              <td>
                {{
                  item.exchanged
                    | currency({ symbol: smbl(item.receiver_currency_id) })
                }}
              </td>
              <td>{{ item.description }}</td>
            </tr>
          </template>
        </template>
      </page>
    </div>
  </print-preview>
</template>

<script>
import get from "lodash/get";
import { mapGetters } from "vuex";

export default {
  computed: {
    ...mapGetters("ReportsModule", { dailyReport: "dailyReport" }),
    headers: (vm) => [
      { text: vm.$t("credit_number") },
      { text: vm.$t("date") },
      { text: vm.$t("customer") },
      { text: vm.$t("amount"), colspan: 2 },
      { text: vm.$t("rate") },
      { text: vm.$t("total") },
      { text: vm.$t("description") },
    ],
    exchange_headers: (vm) => [
      { text: vm.$t("exchange_number") },
      { text: vm.$t("date") },
      { text: vm.$t("customer") },
      { text: vm.$t("sale_currency"), colspan: 2 },
      { text: vm.$t("rate") },
      { text: vm.$t("buy_currency") },
      { text: vm.$t("description") },
    ],

    transfer_headers: (vm) => [
      { text: vm.$t("date") },
      { text: vm.$t("transfer_number") },
      { text: vm.$t("sender") },
      { text: vm.$t("receiver") },
      { text: vm.$t("amount"), colspan: 2 },
      { text: vm.$t("commission") },
      { text: vm.$t("description") },
    ],

    received_transfer_headers: (vm) => [
      { text: vm.$t("date") },
      { text: vm.$t("transfer_number") },
      { text: vm.$t("sender") },
      { text: vm.$t("receiver") },
      { text: vm.$t("amount") },
      { text: vm.$t("commission") },
      { text: vm.$t("status") },
      { text: vm.$t("description") },
    ],

    a2a_transfer_headers: (vm) => [
      { text: vm.$t("date") },
      { text: vm.$t("transfer_number") },
      { text: vm.$t("sender") },
      { text: vm.$t("receiver") },
      { text: vm.$t("amount") },
      { text: vm.$t("rate") },
      { text: vm.$t("total") },
      { text: vm.$t("description") },
    ],

    items() {
      return this.dailyReport.items;
    },

    landscape() {
      return true;
    },

    styles() {
      if (this.landscape) return { "min-height": "200mm", width: "290mm" };
      return { "min-height": "297mm", width: "200mm" };
    },
  },

  methods: {
    getObjectValueByPath(obj, filed) {
      if (filed.computed) return filed.computed(obj);
      let value = get(obj, filed.value, "");
      if (filed.filter) return filed.filter(obj);
      return value;
    },

    reset() {
      this.$store.commit("ReportsModule/setDailyReport", {
        title: "",
        items: [],
        headers: [],
      });
      this.$router.back();
    },
  },
};
</script>